import { useState } from 'react';
import { 
    Trash2, 
    Pen, 
    ChevronDown, 
    ChevronUp,
    Clock,
    Trophy,
    Star,
    Minus,
    MessageSquare,
    Users
} from 'lucide-react';

type ResultsEntryProps = {
    discipline: 'trampoline' | 'dmt' | 'tumbling' | 'synchro';
    result: any;
    setEditing: (type: string, result: any) => void;
    remove: (id: string) => void;
};

const ResultsEntry: React.FC<ResultsEntryProps> = ({ discipline, result, setEditing, remove }) => {
    const [isExpanded, setIsExpanded] = useState(false);

    type Discipline = 'trampoline' | 'dmt' | 'tumbling' | 'synchro';
    type DisciplineConfig = {
        name: string;
        color: string;
        icon: JSX.Element;
        hasTimeOfFlight: boolean;
        hasHD: boolean;
        hasDesynch: boolean;
    };

    const getDisciplineConfig = (discipline: Discipline) => {
        const configs: Record<Discipline, DisciplineConfig> = {
            'trampoline': {
                name: 'Trampoline',
                color: 'from-blue-500 to-blue-600',
                icon: <Trophy className="w-4 h-4" />,
                hasTimeOfFlight: true,
                hasHD: true,
                hasDesynch: false
            },
            'dmt': {
                name: 'DMT',
                color: 'from-green-500 to-green-600',
                icon: <Trophy className="w-4 h-4" />,
                hasTimeOfFlight: false,
                hasHD: false,
                hasDesynch: false
            },
            'tumbling': {
                name: 'Tumbling',
                color: 'from-orange-500 to-orange-600',
                icon: <Trophy className="w-4 h-4" />,
                hasTimeOfFlight: false,
                hasHD: false,
                hasDesynch: false
            },
            'synchro': {
                name: 'Synchro',
                color: 'from-pink-500 to-pink-600',
                icon: <Users className="w-4 h-4" />,
                hasTimeOfFlight: false,
                hasHD: true,
                hasDesynch: true
            }
        };
        return configs[discipline];
    };

    const config = getDisciplineConfig(discipline);

    return (
        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden transition-all duration-300 hover:shadow-xl mb-4">
            {/* Header */}
            <div className={`bg-gradient-to-r ${config.color} p-4 text-white relative`}>
                <div className="absolute inset-0 bg-white opacity-10"></div>
                <div className="relative flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                        {config.icon}
                        <div>
                            <h3 className="font-bold text-lg">
                                {result.event}
                            </h3>
                            {result.result_date && (
                                <p className="text-sm text-white text-opacity-90">
                                    {new Date(result.result_date).toLocaleDateString()}
                                </p>
                            )}
                        </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex items-center space-x-2">
                        <button
                            onClick={() => setEditing(discipline, result)}
                            className="w-8 h-8 bg-white bg-opacity-20 hover:bg-opacity-30 rounded-full flex items-center justify-center transition-all duration-200 hover:scale-110"
                            title="Edit"
                        >
                            <Pen className="w-4 h-4 text-gray-800 hover:text-gray-900" />
                        </button>

                        <button
                            onClick={() => remove(result.id)}
                            className="w-8 h-8 bg-red-500 bg-opacity-80 hover:bg-opacity-100 rounded-full flex items-center justify-center transition-all duration-200 hover:scale-110"
                            title="Delete"
                        >
                            <Trash2 className="w-4 h-4 text-white" />
                        </button>
                    </div>
                </div>
            </div>

            {/* Main Content */}
            <div className="p-6">
                {/* Quick Stats */}
                {!isExpanded && (
                    <div className="grid grid-cols-1 gap-4 mb-4">
                        <div className="bg-gray-50 rounded-lg p-3">
                            <div className="text-sm text-gray-500">Total Score</div>
                            <div className="text-2xl font-bold text-gray-800">
                                {result.total_score}
                            </div>
                        </div>
                    </div>
                )}

                {/* Expanded Details */}
                <div className={`transition-all duration-300 overflow-hidden ${
                    isExpanded ? 'max-h-none opacity-100' : 'max-h-0 opacity-0'
                }`}>
                    <div className="space-y-4 pt-4 border-t border-gray-100">
                        {/* Performance Metrics */}
                        <div className="grid grid-cols-2 gap-4">
                            <div className="bg-purple-50 rounded-lg p-4 flex items-center space-x-3">
                                <Star className="w-5 h-5 text-purple-500" />
                                <div>
                                    <div className="text-sm text-purple-600 font-medium">Execution</div>
                                    <div className="text-lg font-bold text-purple-800">
                                        {result.execution}
                                    </div>
                                </div>
                            </div>

                            <div className="bg-blue-50 rounded-lg p-4 flex items-center space-x-3">
                                <Trophy className="w-5 h-5 text-blue-500" />
                                <div>
                                    <div className="text-sm text-blue-600 font-medium">Difficulty</div>
                                    <div className="text-lg font-bold text-blue-800">
                                        {result.difficulty}
                                    </div>
                                </div>
                            </div>

                            {(config.hasTimeOfFlight && result.tof > 0) && (
                                <div className="bg-blue-50 rounded-lg p-4 flex items-center space-x-3">
                                    <Clock className="w-5 h-5 text-blue-500" />
                                    <div>
                                        <div className="text-sm text-blue-600 font-medium">Time of Flight</div>
                                        <div className="text-lg font-bold text-blue-800">
                                            {result.tof}s
                                        </div>
                                    </div>
                                </div>
                            )}

                            {(config.hasHD && result.hd > 0) && (
                                <div className="bg-green-50 rounded-lg p-4 flex items-center space-x-3">
                                    <Trophy className="w-5 h-5 text-green-500" />
                                    <div>
                                        <div className="text-sm text-green-600 font-medium">HD Score</div>
                                        <div className="text-lg font-bold text-green-800">
                                            {result.hd}
                                        </div>
                                    </div>
                                </div>
                            )}

                            {(config.hasDesynch && result.desynch > 0) && (
                                <div className="bg-pink-50 rounded-lg p-4 flex items-center space-x-3">
                                    <Users className="w-5 h-5 text-pink-500" />
                                    <div>
                                        <div className="text-sm text-pink-600 font-medium">Desynch</div>
                                        <div className="text-lg font-bold text-pink-800">
                                            {result.desynch}
                                        </div>
                                    </div>
                                </div>
                            )}

                            {result.penalty > 0 && (
                                <div className="bg-red-50 rounded-lg p-4 flex items-center space-x-3 col-span-2">
                                    <Minus className="w-5 h-5 text-red-500" />
                                    <div>
                                        <div className="text-sm text-red-600 font-medium">Penalty</div>
                                        <div className="text-lg font-bold text-red-800">
                                            {result.penalty}
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Total Score in expanded view */}
                        <div className="bg-gray-50 rounded-lg p-4">
                            <div className="text-sm text-gray-500 font-medium">Total Score</div>
                            <div className="text-3xl font-bold text-gray-800">
                                {result.total_score}
                            </div>
                        </div>

                        {/* Comments Section */}
                        {result.comment && result.comment.trim() && (
                            <div className="bg-amber-50 rounded-lg p-4">
                                <div className="flex items-center space-x-2 mb-2">
                                    <MessageSquare className="w-5 h-5 text-amber-600" />
                                    <span className="font-semibold text-amber-800 text-sm">Comments</span>
                                </div>
                                <p className="text-base text-amber-700 leading-relaxed">
                                    {result.comment}
                                </p>
                            </div>
                        )}
                    </div>
                </div>

                {/* Toggle Button */}
                <button
                    onClick={() => setIsExpanded(!isExpanded)}
                    className="w-full mt-4 py-3 bg-gray-50 hover:bg-gray-100 rounded-lg transition-all duration-200 flex items-center justify-center space-x-2 font-medium text-gray-700"
                >
                    <span className="text-base">{isExpanded ? 'Show Less' : 'Show More'}</span>
                    {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                </button>
            </div>
        </div>
    );
};

export default ResultsEntry;